import { Controller } from "@hotwired/stimulus"

// Shows/hides fields based on the selected event_type.
//
// Supports two control shapes:
//   1. A single <select> target with options one_time / recurring / series.
//   2. A group of radio inputs (segmented control) all sharing the
//      `select` target identifier — the currently-checked one provides
//      the value.
//
// Usage:
//   <div data-controller="event-type-toggle">
//     <select data-event-type-toggle-target="select"
//             data-action="change->event-type-toggle#onChange">…</select>
//     <!-- OR a segmented radio control: -->
//     <input type="radio" name="event[event_type]" value="one_time"
//            data-event-type-toggle-target="select"
//            data-action="change->event-type-toggle#onChange">
//     ...
//
//     <div data-event-type-toggle-target="firstDateField">…</div>           // hidden for series
//     <div data-event-type-toggle-target="recurringField">…</div>           // recurring only
//     <div data-event-type-toggle-target="advancedRecurrenceField">…</div>  // recurring only
//     <div data-event-type-toggle-target="seriesField">…</div>              // series only
//   </div>
export default class extends Controller {
  static targets = [
    "select",
    "firstDateField",
    "recurringField",
    "advancedRecurrenceField",
    "seriesField",
  ]

  declare readonly selectTargets: Array<HTMLSelectElement | HTMLInputElement>
  declare readonly hasSelectTarget: boolean
  declare readonly firstDateFieldTarget: HTMLElement
  declare readonly hasFirstDateFieldTarget: boolean
  declare readonly recurringFieldTarget: HTMLElement
  declare readonly hasRecurringFieldTarget: boolean
  declare readonly advancedRecurrenceFieldTarget: HTMLElement
  declare readonly hasAdvancedRecurrenceFieldTarget: boolean
  declare readonly seriesFieldTarget: HTMLElement
  declare readonly hasSeriesFieldTarget: boolean

  connect() {
    this.sync()
  }

  onChange() {
    this.sync()
  }

  private sync() {
    if (!this.hasSelectTarget) return
    const value = this.currentValue()

    // A series names its own dates part by part, so a first date would be a
    // second answer to the same question. The input stays in the DOM — the
    // schedule controller still reads its clock times.
    if (this.hasFirstDateFieldTarget) {
      this.firstDateFieldTarget.classList.toggle("hidden", value === "series")
    }
    if (this.hasRecurringFieldTarget) {
      this.recurringFieldTarget.classList.toggle("hidden", value !== "recurring")
    }
    if (this.hasAdvancedRecurrenceFieldTarget) {
      this.advancedRecurrenceFieldTarget.classList.toggle("hidden", value !== "recurring")
    }
    if (this.hasSeriesFieldTarget) {
      this.seriesFieldTarget.classList.toggle("hidden", value !== "series")
    }
  }

  private currentValue(): string {
    // If any of the targets is a radio, return the checked one's value.
    // Otherwise return the (single) target's value (select-style).
    const radios = this.selectTargets.filter(el => (el as HTMLInputElement).type === "radio") as HTMLInputElement[]
    if (radios.length > 0) {
      const checked = radios.find(r => r.checked)
      return checked ? checked.value : ""
    }
    return this.selectTargets[0]?.value ?? ""
  }
}
